import { lazy, Suspense } from 'react';
import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
import { MarketingLayout } from './components/layout/MarketingLayout';
import { RequireAuth } from './components/layout/RequireAuth';
import { RouteFallback } from './components/layout/RouteFallback';
import { NotFound } from './features/misc/NotFound';

const RELOAD_FLAG = 'once.chunkReload';

/**
 * lazy(), but survives a deploy that happened while the tab was open.
 *
 * Chunk filenames carry a content hash, so a released build removes the ones
 * the loaded document points at. A tab opened before that deploy - or served a
 * stale index.html by the service worker - then fails on the first navigation
 * with "Failed to fetch dynamically imported module", and the whole app dies
 * on a routine deploy.
 *
 * Reloading resolves it: the fresh index.html names the chunks that exist. The
 * sessionStorage flag makes that a one-shot, so a chunk missing for any other
 * reason surfaces as an error instead of an infinite reload loop.
 */
function lazyRoute(load: () => Promise<{ default: ComponentType }>) {
  return lazy(async () => {
    try {
      const mod = await load();
      sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (error) {
      if (sessionStorage.getItem(RELOAD_FLAG)) throw error;
      sessionStorage.setItem(RELOAD_FLAG, '1');
      window.location.reload();
      // The page is going away; never resolve, so nothing renders meanwhile.
      return new Promise<{ default: ComponentType }>(() => {});
    }
  });
}

/* Sender surface - eagerly grouped, it is one coherent app. */
const Landing = lazyRoute(() => import('./features/marketing/Landing'));
const SignIn = lazyRoute(() => import('./features/auth/SignIn'));
const SignUp = lazyRoute(() => import('./features/auth/SignUp'));
const ResetPassword = lazyRoute(() => import('./features/auth/ResetPassword'));
const AuthCallback = lazyRoute(() => import('./features/auth/AuthCallback'));
const Dashboard = lazyRoute(() => import('./features/sender/Dashboard'));
const NewUpload = lazyRoute(() => import('./features/sender/NewUpload'));
const VideoDetail = lazyRoute(() => import('./features/sender/VideoDetail'));
const AccountSettings = lazyRoute(() => import('./features/sender/AccountSettings'));

/*
 * Viewer surface - deliberately its own chunk. It must not pull in the sender
 * shell, the dashboard queries or anything that assumes a signed-in account.
 */
const ViewerRoute = lazyRoute(() => import('./features/viewer/ViewerRoute'));

function Suspended() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Outlet />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <Suspended />,
    children: [
      {
        element: <MarketingLayout />,
        children: [
          { path: '/', element: <Landing /> },
          { path: '/auth/sign-in', element: <SignIn /> },
          { path: '/auth/sign-up', element: <SignUp /> },
          { path: '/auth/reset', element: <ResetPassword /> },
          { path: '/auth/callback', element: <AuthCallback /> },
        ],
      },
      {
        path: '/app',
        element: (
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <Dashboard /> },
          { path: 'upload', element: <NewUpload /> },
          { path: 'videos/:videoId', element: <VideoDetail /> },
          { path: 'settings', element: <AccountSettings /> },
        ],
      },
      /*
       * The secret travels in the URL fragment (`/v/:publicId#s=…`), which the
       * browser never sends to a server. The route only sees the public id.
       */
      { path: '/v/:publicId', element: <ViewerRoute /> },
      { path: '/dashboard', element: <Navigate to="/app" replace /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
